import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api";
import type {
  CompanyConfigCreate,
  CompanyConfigVersionCreate,
} from "../../generated/core-api";

/**
 * Discriminated input for saving a company configuration:
 * - `create` mints a brand-new config + version 1 (`api.createCompanyConfig`).
 * - `version` saves a new version onto an existing config
 *   (`api.saveCompanyConfigVersion`).
 *
 * There is no distinct "draft" state on the backend — both paths publish a
 * version immediately.
 */
export type SaveCompanyConfigInput =
  | { mode: "create"; body: CompanyConfigCreate }
  | { mode: "version"; configCode: string; body: CompanyConfigVersionCreate };

/**
 * Create a company configuration (version 1) or save a new version onto an
 * existing one.
 * Invalidates the list and the affected config on success. Wraps `api.*` from
 * `src/api`; see `src/org/hooks/OrgSource/useCreateSource.ts` for the pattern.
 */
export function useSaveCompanyConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: SaveCompanyConfigInput) =>
      input.mode === "create"
        ? api.createCompanyConfig(input.body)
        : api.saveCompanyConfigVersion(input.configCode, input.body),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["companyConfigs"] });
      queryClient.invalidateQueries({
        queryKey: ["companyConfig", result.config_code],
      });
    },
  });
}
